import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import { capitalize, forEach, get, isArray, isEmpty } from "lodash";
import { Children, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Error } from "@krea/customer-features/shared-components/fields/error";
import Fieldset from "@krea/customer-features/shared-components/fields/fieldset";
import { FileInput } from "@krea/customer-features/shared-components/fields/file";
import FileItem from "@krea/customer-features/shared-components/fields/file-item";
import FilesList from "@krea/customer-features/shared-components/fields/files-list";
import Radio from "@krea/customer-features/shared-components/fields/radio";
import RadioGroup from "@krea/customer-features/shared-components/fields/radiogroup";
import { Helper } from "@krea/customer-features/shared-components/helper";
import { Text } from "@krea/customer-features/shared-components/text";
import {
  useDeleteFile,
  usePostFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";

import FieldRequestDelegateForm from "../FieldRequestDelegateForm";
import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const SELECTED_CHOICE = {
  MANUALLY: "MANUALLY",
  DELEGATE: "DELEGATE",
};

const getDesiredAccountingDataTimeframe = () => {
  const dateFormat = "MMMM YYYY";
  const timePeriodTo = dayjs().subtract(2, "month").format(dateFormat);

  return {
    to: timePeriodTo,
  };
};

const AccountingDataForm = ({
  fieldRequestID,
  isPreviewMode,
  isDelegated,
  withInputLabel,
  manuallyOnly = false,
  onSuccessfulSubmit = () => {},
}) => {
  const { t } = useTranslation();

  const { data: fieldRequest = {} } = useGetOneFieldRequest(
    fieldRequestID,
    isDelegated,
  );

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const { mutate: deleteFile } = useDeleteFile();

  const {
    displayName,
    submittedData,
    isSubmitted,
    delegatedInformationResources,
  } = fieldRequest;

  const files = submittedData?.files || [];
  const errors = error?.data.errors || [];

  let selectedChoiceInit = SELECTED_CHOICE.MANUALLY;

  // If the logged in user has delegated the question, prefill to DELEGATE
  if (
    !isDelegated &&
    !isSubmitted &&
    delegatedInformationResources?.length > 0
  ) {
    selectedChoiceInit = SELECTED_CHOICE.DELEGATE;
  }

  // If user has submitted any files, prefill to MANUALLY
  if (submittedData && Object.keys(submittedData).length > 0) {
    if (submittedData.files && submittedData.files.length > 0) {
      selectedChoiceInit = SELECTED_CHOICE.MANUALLY;
    }
  }

  // If a delegated "user" is viewing the form, always use MANUALLY
  if (isDelegated) {
    selectedChoiceInit = SELECTED_CHOICE.MANUALLY;
  }

  const [selectedChoice, setSelectedChoice] = useState(selectedChoiceInit);

  const showDropzone = selectedChoice === SELECTED_CHOICE.MANUALLY;

  const { to: timePeriodTo } = getDesiredAccountingDataTimeframe();

  const deleteFileFunc = (fileID) => {
    deleteFile(
      { fileID, fieldRequestID, isDelegated },
      {
        onSuccess: () => {
          onSuccessfulSubmit();
        },
      },
    );
  };

  const createFormData = (value) => {
    const formData = new FormData();

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append("files", file, file.name);
      });
    }

    return formData;
  };

  const postDataManually = (currentFiles) => {
    const payload = {
      files: currentFiles || files,
    };

    if (payload.files?.length) {
      const formData = createFormData(payload);
      postFieldRequest(formData, {
        onSuccess: () => {
          onSuccessfulSubmit();
        },
      });
    }
  };

  const toggleRadioButton = ({ target }) => {
    const { value } = target;

    setSelectedChoice(value);
  };

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader className={""} label={displayName} />

          {files && !!files.length && (
            <FieldRequestPreviewHeader
              subheader
              label={t("iaq.uploadedFiles")}
              value={
                <>
                  {files.length > 0 && (
                    <AnimatePresence>
                      {Children.toArray(
                        files.map((file, index) => {
                          return (
                            <FileItem
                              key={`accounting-data-file-${index}`}
                              file={file}
                              deleteFile={deleteFileFunc}
                              isPreviewMode={isPreviewMode}
                            />
                          );
                        }),
                      )}
                    </AnimatePresence>
                  )}
                </>
              }
            />
          )}
        </>
      ) : (
        <>
          <form className="input-form" onSubmit={(e) => e.preventDefault()}>
            <Fieldset>
              <>
                {/*
              Render radio buttons if its a normal user. If its a delegated "user" there is not need to show radio
              buttons since the choice is always MANUALLY (see selectedChoiceInit above)
              */}

                {!isDelegated ||
                  (!manuallyOnly && (
                    <RadioGroup
                      value={selectedChoice}
                      onChange={toggleRadioButton}
                    >
                      <Radio
                        value={SELECTED_CHOICE.MANUALLY}
                        id="accounting-manually"
                        name="accounting-radio-button"
                        label={t("iaq.accountingData.uploadManually")}
                        checked={SELECTED_CHOICE.MANUALLY === selectedChoice}
                      />

                      <Radio
                        value={SELECTED_CHOICE.DELEGATE}
                        id="accounting-delegate"
                        name="accounting-radio-button"
                        label={t("iaq.accountingData.delegateChoice")}
                        checked={SELECTED_CHOICE.DELEGATE === selectedChoice}
                      />
                    </RadioGroup>
                  ))}

                {(showDropzone || files.length > 0) && (
                  <>
                    <Text
                      size="sm"
                      className="d-flex flex-wrap font-weight-bold text-warning"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      <Trans
                        i18nKey="iaq.accountingData.accountingManually.periodText"
                        components={{
                          span: <span />,
                          helper: (
                            <Helper
                              className="ml-1 mt-n1"
                              content={t(
                                "iaq.accountingData.accountingManually.helper",
                              )}
                            />
                          ),
                          to: capitalize(timePeriodTo),
                        }}
                      />
                    </Text>

                    {showDropzone && (
                      <FileInput
                        multiple
                        id="balance-sheet"
                        files={files}
                        postFiles={postDataManually}
                        error={get(errors, "[0].message", null)}
                      />
                    )}

                    {files.length > 0 && (
                      <FilesList label={t("iaq.uploadedFiles")}>
                        <AnimatePresence>
                          {Children.toArray(
                            files.map((file, index) => {
                              return (
                                <FileItem
                                  key={`accounting-data-file-${index}`}
                                  file={file}
                                  deleteFile={deleteFileFunc}
                                  isPreviewMode={isPreviewMode}
                                />
                              );
                            }),
                          )}
                        </AnimatePresence>
                      </FilesList>
                    )}
                  </>
                )}

                {!isDelegated &&
                  selectedChoice === SELECTED_CHOICE.DELEGATE && (
                    <FieldRequestDelegateForm fieldRequestID={fieldRequestID} />
                  )}
              </>
            </Fieldset>
          </form>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default AccountingDataForm;
